import { Injectable } from '@angular/core';
import { HttpClient, HttpEventType } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { HttpHelperService } from './httphelper.service';
import { catchError, filter, map } from 'rxjs/operators';

export interface FileUploadResponse {
	path: string;
	message?: string;
}

@Injectable({
	providedIn: 'root'
})
export class FileUploadService {
	private baseUrl = environment.apiUrl + 'fileupload';

	constructor(private http: HttpClient,
		private httpHelperService: HttpHelperService,
	) { }

	/**
	 * Upload an icon file (SVG, PNG, etc.)
	 * @param file The icon file to upload
	 * @returns Observable with the uploaded file path
	 */
	uploadIcon(file: File): Observable<FileUploadResponse> {
		const formData = new FormData();
		formData.append('icon', file);
		const options = this.httpHelperService.getHttpOptionsWithMultiPart();

		return this.http.post<FileUploadResponse>(`${this.baseUrl}/icon`, formData, options).pipe(
			filter((event: any) => event.type === HttpEventType.Response),
			map((event: any) => {
				const result = event.body as FileUploadResponse;
				return result;
			}),
			catchError((error) => throwError(error))
		);;
	}

	/**
	 * Upload a JSON file
	 * @param file The JSON file to upload
	 * @returns Observable with the uploaded file path
	 */
	uploadJson(file: File): Observable<FileUploadResponse> {
		const formData = new FormData();
		formData.append('file', file);
		return this.http.post<FileUploadResponse>(`${this.baseUrl}/json`, formData);
	}

	/**
	 * Upload an image file
	 * @param file The image file to upload
	 * @returns Observable with the uploaded file path
	 */
	uploadImage(file: File): Observable<FileUploadResponse> {
		const formData = new FormData();
		formData.append('file', file);
		return this.http.post<FileUploadResponse>(`${this.baseUrl}/image`, formData);
	}

	/**
	 * Upload a document file (PDF, DOC, etc.)
	 * @param file The document file to upload
	 * @returns Observable with the uploaded file path
	 */
	uploadDocument(file: File): Observable<FileUploadResponse> {
		const formData = new FormData();
		formData.append('file', file);
		return this.http.post<FileUploadResponse>(`${this.baseUrl}/document`, formData);
	}

	/**
	 * Upload a CSV file
	 * @param file The CSV file to upload
	 * @returns Observable with the uploaded file path
	 */
	uploadCsv(file: File): Observable<FileUploadResponse> {
		const formData = new FormData();
		formData.append('file', file);
		return this.http.post<FileUploadResponse>(`${this.baseUrl}/csv`, formData);
	}

	/**
	 * Generic file upload method
	 * @param file The file to upload
	 * @param type The file type endpoint (icon, json, image, etc.)
	 * @returns Observable with the uploaded file path
	 */
	uploadFile(file: File, type: string): Observable<FileUploadResponse> {
		const formData = new FormData();
		formData.append('file', file);
		return this.http.post<FileUploadResponse>(`${this.baseUrl}/${type}`, formData);
	}
}
